// Reading the menu the server sent: which rows the web shows, which section a row
// belongs to, and which row an address is.
//
// The hiding rules below are the ones the top bar has always applied. They live here so
// the home screen, the section front pages, the trail and page search apply the SAME
// rules — a page the top bar hides must not be advertised anywhere else.
import type { MenuItem } from '../stores/menuStore';
import { normalisePath } from './catalog';
import { DASHBOARD_HOME_PATH } from './dashboards';

export interface MenuRules {
  isSuperAdmin: boolean;
  selectedProjectId: number | null;
}

const lower = (v: unknown) => String(v ?? '').toLowerCase().trim();

/** Rows the web never shows to this person, whatever the server sent. */
export function isHiddenMenu(m: MenuItem, rules: MenuRules): boolean {
  const name = lower(m.name);
  const code = lower(m.code);
  const path = lower(m.path);
  if (path === '/settings/menus') return true;
  if (code === 'menu_management' || code === 'menus') return true;
  if (name === 'menu management') return true;
  // Admin Users is managed in the database only.
  if (path === '/settings/admin-users' || code === 'admin_users') return true;
  // Project Management is for super admins only.
  if (!rules.isSuperAdmin && (code === 'project_management' || path === '/settings/projects')) return true;
  // Apple-only entries are scoped to project 170.
  if ((code === 'apple_scorecard' || code === 'employee_quadrants') && rules.selectedProjectId !== 170) return true;
  // "Raise & cancel hiring" is an Access Management row, not a page: it opens nothing. (The
  // server already leaves it out of every menu; this keeps an older answer from drawing it.)
  if (code === RIGHT_ONLY_MENU_CODE) return true;
  return false;
}

/** The one Access Management row that is a right and not a page (apps/api services/page-rights.ts HIRING_RAISE_ROW_CODE). */
export const RIGHT_ONLY_MENU_CODE = 'hiring_raise';

/** A per-dashboard access row (dashboard_deployment …). Never a page of its own in the menu. */
export const isDashboardTabMenu = (m: MenuItem): boolean => lower(m.code).startsWith('dashboard_');

/** Active rows this person may see as PAGES: hidden rows and per-dashboard access rows removed. */
export function pageMenuItems(items: MenuItem[], rules: MenuRules): MenuItem[] {
  return items.filter((m) => m.is_active && !isHiddenMenu(m, rules) && !isDashboardTabMenu(m));
}

/** Menu order: display_order, then name; Settings always last among top-level rows. */
export function compareMenu(a: MenuItem, b: MenuItem): number {
  const order = (m: MenuItem) => (lower(m.code) === 'settings' ? 9999 : m.display_order ?? 0);
  return order(a) - order(b) || String(a.name).localeCompare(String(b.name));
}

export function menuChildren(pages: MenuItem[], parentId: string | null): MenuItem[] {
  return pages.filter((m) => (m.parent_id ?? null) === parentId).sort(compareMenu);
}

/** Every page under a top-level row, at any depth, in menu order. Rows without an address are headings and are skipped. */
export function pagesUnder(pages: MenuItem[], top: MenuItem): MenuItem[] {
  const out: MenuItem[] = [];
  const walk = (parentId: string) => {
    for (const child of menuChildren(pages, parentId)) {
      if (child.path) out.push(child);
      walk(child.id);
    }
  };
  walk(top.id);
  return out;
}

/** The top-level row a row sits under (itself when it is top-level). */
export function topLevelOf(pages: MenuItem[], m: MenuItem): MenuItem {
  let current = m;
  const seen = new Set<string>();
  while (current.parent_id && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = pages.find((p) => p.id === current.parent_id);
    if (!parent) break;
    current = parent;
  }
  return current;
}

/** The menu row for an address, ignoring its query string. Null for the home screen, dashboards and detail screens. */
export function menuForPath(pages: MenuItem[], pathname: string): MenuItem | null {
  const wanted = normalisePath(pathname);
  return pages.find((m) => m.path && normalisePath(m.path) === wanted) ?? null;
}

/** The menu codes a person holds, for the per-dashboard access check. */
export function menuCodes(items: MenuItem[]): Set<string> {
  return new Set(items.filter((m) => m.is_active).map((m) => String(m.code || '')));
}

/** True when the person holds the Dashboard itself (any row pointing at /dashboard). */
export function holdsDashboard(items: MenuItem[]): boolean {
  return items.some((m) => m.is_active && m.path === '/dashboard');
}

/**
 * The first page a person holds, in menu order: top-level rows by display_order, and
 * under each one its first child with an address (a parent with children opens on its
 * first child, as in the top bar: My Approvals → Approvals / Probation Confirmation),
 * else the row's own address. Null when no row the person holds has an address.
 */
export function firstPagePath(items: MenuItem[]): string | null {
  const topLevel = items
    .filter((m) => !m.parent_id && m.is_active)
    .sort((a, b) => a.display_order - b.display_order);
  for (const menu of topLevel) {
    const children = items
      .filter((m) => m.parent_id === menu.id && m.is_active)
      .sort((a, b) => a.display_order - b.display_order);
    for (const child of children) {
      if (child.path) return child.path;
    }
    if (menu.path) return menu.path;
  }
  return null;
}

/**
 * The person's own home page: where the app takes them after sign-in or when they open
 * "/". Both of those arrive at the Dashboard, and the shell sends anyone who does not
 * hold it on to their first page. Null until the menu says where that is.
 */
export function homePath(items: MenuItem[]): string | null {
  return holdsDashboard(items) ? DASHBOARD_HOME_PATH : firstPagePath(items);
}
